import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { type DocumentSummary } from '../../../lib/types';

interface UseUploadDocumentResult {
  upload: (file: File, opts?: { name?: string; type?: string }) => Promise<DocumentSummary>;
  isUploading: boolean;
  error: Error | null;
  reset: () => void;
}

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25MB (D6)
const ALLOWED_MIMES = new Set<string>([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'video/mp4',
  'audio/mpeg',
  'audio/mp4',
  'audio/ogg',
  'audio/wav',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

export class FileTooLargeError extends Error {
  constructor(public readonly actualBytes: number) {
    super(`Arquivo excede limite de 25MB (atual: ${(actualBytes / 1024 / 1024).toFixed(1)}MB).`);
    this.name = 'FileTooLargeError';
  }
}

export class UnsupportedMimeError extends Error {
  constructor(public readonly mimeType: string) {
    super(`Tipo de arquivo não suportado: ${mimeType}.`);
    this.name = 'UnsupportedMimeError';
  }
}

/**
 * Mutation para upload de Document via Picker (Item 36 PR-B2).
 *
 * Validação client-side:
 * - Size <= 25MB (D6)
 * - MIME whitelist Meta-aligned
 *
 * No sucesso, invalida cache do Picker do bondId atual.
 */
export function useUploadDocument(bondId?: string): UseUploadDocumentResult {
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    DocumentSummary & { deduplicated: boolean },
    Error,
    { file: File; name?: string; type?: string }
  > = useMutation({
    mutationFn: async ({ file, name, type }) => {
      if (file.size > MAX_UPLOAD_BYTES) {
        throw new FileTooLargeError(file.size);
      }
      if (!ALLOWED_MIMES.has(file.type)) {
        throw new UnsupportedMimeError(file.type);
      }
      return api.uploadDocument(file, {
        name: name ?? file.name,
        ...(type !== undefined && { type }),
      });
    },
    onSuccess: () => {
      if (bondId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: ['documents', 'picker', bondId] });
      }
    },
  });

  return {
    upload: async (file, opts) => mutation.mutateAsync({ file, ...(opts ?? {}) }),
    isUploading: mutation.isPending,
    error: mutation.error,
    reset: () => mutation.reset(),
  };
}
